"use client";

import { useMemo } from "react";
import { useRouter } from "@/lib/navigation";
import { useWebMcpTools, requireWebMcpString } from "@/hooks/use-webmcp-tools";
import type { PortalPageKey, PortalPageState } from "@/lib/portal-pages";
import { brandNames } from "@/lib/brand";

type AtlasWebMcpSection = {
  key: string;
  label: string;
  description: string;
  href: string;
};

export function AtlasWebMcpNavigation({
  accessiblePageKeys,
  advancedAgentEnabled,
  capacityRiskTrackerEnabled,
  privateVideosEnabled = false,
  enabled,
  notesFeedEnabled,
  pages,
}: {
  accessiblePageKeys: readonly PortalPageKey[];
  advancedAgentEnabled: boolean;
  capacityRiskTrackerEnabled: boolean;
  privateVideosEnabled?: boolean;
  enabled: boolean;
  notesFeedEnabled: boolean;
  pages: readonly PortalPageState[];
}) {
  const router = useRouter();
  const sections = useMemo(
    () =>
      resolveAtlasWebMcpSections({
        accessiblePageKeys,
        advancedAgentEnabled,
        capacityRiskTrackerEnabled,
        privateVideosEnabled,
        notesFeedEnabled,
        pages,
      }),
    [
      accessiblePageKeys,
      advancedAgentEnabled,
      capacityRiskTrackerEnabled,
      privateVideosEnabled,
      notesFeedEnabled,
      pages,
    ],
  );
  const tools = useMemo(() => {
    const sectionsByKey = new Map(sections.map((section) => [section.key, section]));
    return [
      {
        name: "atlas_navigate",
        title: "Open an Atlas section",
        description:
          "Opens a CS Atlas section available to the signed-in member. Use this for visible portal navigation, including Notes and Support.",
        inputSchema: {
          type: "object",
          properties: {
            section: {
              type: "string",
              description: "The accessible Atlas section to open.",
              oneOf: sections.map((section) => ({
                const: section.key,
                title: section.label,
              })),
            },
          },
          required: ["section"],
          additionalProperties: false,
        },
        annotations: {
          readOnlyHint: true,
          untrustedContentHint: false,
        },
        execute: async (input: Record<string, unknown>) => {
          const sectionKey = requireWebMcpString(input, "section", {
            maxLength: 80,
          });
          const section = sectionsByKey.get(sectionKey);
          if (!section) {
            throw new Error("That Atlas section is not available to this member.");
          }
          router.push(section.href);
          return {
            ok: true,
            section: section.key,
            title: section.label,
            href: section.href,
          };
        },
      },
    ];
  }, [router, sections]);

  useWebMcpTools(enabled && sections.length > 0, tools);
  return null;
}

export function resolveAtlasWebMcpSections({
  accessiblePageKeys,
  advancedAgentEnabled,
  capacityRiskTrackerEnabled,
  privateVideosEnabled = false,
  notesFeedEnabled,
  pages,
}: {
  accessiblePageKeys: readonly PortalPageKey[];
  advancedAgentEnabled: boolean;
  capacityRiskTrackerEnabled: boolean;
  privateVideosEnabled?: boolean;
  notesFeedEnabled: boolean;
  pages: readonly PortalPageState[];
}): AtlasWebMcpSection[] {
  const accessible = new Set(accessiblePageKeys);
  const sections: AtlasWebMcpSection[] = pages
    .filter(
      (page) =>
        accessible.has(page.key) &&
        page.status === "live" &&
        page.globallyEnabled &&
        !page.hidden &&
        (page.key !== "capacityRiskTracker" || capacityRiskTrackerEnabled) &&
        (page.key !== "videos" || privateVideosEnabled) &&
        (page.key !== "advancedAgent" || advancedAgentEnabled),
    )
    .map(({ key, label, description, href }) => ({
      key,
      label,
      description,
      href,
    }));

  if (
    advancedAgentEnabled &&
    accessible.has("advancedAgent") &&
    !sections.some((section) => section.key === "advancedAgent")
  ) {
    sections.push({
      key: "advancedAgent",
      label: brandNames.agentProShort,
      description: "Deeper research and analysis with web search and files.",
      href: "/advanced-agent",
    });
  }
  if (notesFeedEnabled) {
    sections.push({
      key: "notes",
      label: "Notes",
      description: "Creative Strategies research notes and market commentary.",
      href: "/notes",
    });
  }
  sections.push({
    key: "support",
    label: "Support",
    description: "Account, subscription, and research support.",
    href: "/dashboard/support",
  });
  return sections;
}
